import React, { Component } from "react";

import { ActionPermissionsObject } from "common/interfaces";
import { AiImageModel } from "common/aiConstants";
import { getCanvasBundle } from "js/canvas";
import { appVersion } from "js/config";
import WorkspaceController from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import { ds } from "js/core/models/dataService";
import { Key } from "js/core/utilities/keys";
import { trackActivity } from "js/core/utilities/utilities";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import PresentationEditorController from "js/editor/PresentationEditor/PresentationEditorController";
import UpgradeIcon from "js/Components/UpgradeIcon";
import { ShowErrorDialog, ShowMessageDialog } from "js/react/components/Dialogs/BaseDialog";
import ProBadge from "js/react/components/ProBadge";
import {
    Divider,
    UIContainer,
    UINavigation,
    UINavigationItem
} from "js/react/components/UiComponents";
import { AddSlideController } from "js/react/views/AddSlide/AddSlideController";
import { SearchSlidesPane } from "js/react/views/AddSlide/Panes/SearchSlidesPane";
import GenerateSlidePane from "js/react/views/AIGeneration/panes/GenerateSlidePane/GenerateSlidePane";
import { UpgradePlanDialogType } from "js/react/views/MarketingDialogs/UpgradePlanDialog";
import { TeamResourceTypes } from "js/react/views/TeamResources/TeamResources";
import { $, _ } from "js/vendor";
import { CopySlidePane } from "./Panes/CopySlidePane";
import { ImportSlidesFromPPTPane } from "./Panes/ImportSlidesFromPPTPane";
import { SharedSlidesPane } from "./Panes/SharedSlidesPane";
import { SlideGalleryPane } from "./Panes/SlideGalleryPane";

import "css/add-slide.scss";
import AIIcon from "js/react/components/AIIcon";

class AddSlideContainer extends Component {
    state = {
        selectedPaneId: "search",
        showUserTheme: false
    };

    constructor() {
        super();

        this.searchSlidesPaneRef = React.createRef();
    }

    componentDidMount() {
        PresentationEditorController.setPopupState(true);
        $(window).on("keydown.add-slide-container", event => {
            if (event.which == Key.ESCAPE) {
                this.handleBackButton();
            }
        });
    }

    componentWillUnmount() {
        PresentationEditorController.setPopupState(false);
        $(window).off("keydown.add-slide-container");
    }

    handleSelectPane = paneId => {
        if (paneId == "search" && this.state.selectedPaneId == "search") {
            if (this.searchSlidesPaneRef.current.state.query != "") {
                this.searchSlidesPaneRef.current.handleClearSearch();
                return;
            }
            if (this.searchSlidesPaneRef.current.state.selectedTemplate != null) {
                this.searchSlidesPaneRef.current.setState({ selectedTemplate: null });
                return;
            }
        }

        this.setState({ selectedPaneId: paneId });
    }

    handleBackButton = () => {
        const { closeDialog } = this.props;

        if (this.state.selectedPresentationId) {
            this.setState({
                selectedPresentationId: null
            });
            return;
        }

        if (this.state.selectedPaneId == "search" && this.searchSlidesPaneRef.current.state.query != "") {
            this.searchSlidesPaneRef.current.handleClearSearch();
            return;
        }
        if (this.state.selectedPaneId == "search" && this.searchSlidesPaneRef.current.state.selectedTemplate != null) {
            this.searchSlidesPaneRef.current.setState({
                selectedTemplate: null
            });
            return;
        }

        closeDialog(false);
    }

    handleAddSlideFromTemplate = async template => {
        if (this.isClosing) return;
        this.isClosing = true;

        await AddSlideController.addSlideFromTemplate(template, this.props.replaceSlide);
        _.defer(() => {
            this.props.closeDialog(true);
        });
    }

    handleAddSlideFromTeamSlideTemplate = async teamSlideTemplates => {
        if (this.isClosing) return;
        this.isClosing = true;

        await withProgressDialog({
            title: "Adding selected slides to presentation",
            action: async () => {
                await Promise.all(teamSlideTemplates.map(async teamSlideTemplate => {
                    await AddSlideController.addSlideFromTeamSlideTemplate(teamSlideTemplate);
                }));
            }
        });
        _.defer(() => {
            this.props.closeDialog(true);
        });
    }

    handleToggleUserTheme = () => {
        this.setState({ showUserTheme: !this.state.showUserTheme });
    }

    handleAddBlankClassicSlide = async () => {
        const { slideTemplates: { classicTemplates } } = await getCanvasBundle(window.FORCE_NEW_SLIDES_VERSION ?? appVersion);
        const blank = classicTemplates.find(template => template.id === "authoring");
        return this.handleAddSlideFromClassicTemplate(blank);
    }

    handleAddSlideFromClassicTemplate = async template => {
        if (this.isClosing) return;
        this.isClosing = true;

        const slideModel = {
            template_id: "authoring",
            layout: {
                headerPosition: template.headerPosition ?? "none",
                showFooter: "off"
            },
            states: [{
                primary: template.model,
                header: template.headerModel
            }],
            version: template.version,
            migrationVersion: template.migrationVersion
        };

        AddSlideController.addSlideFromModel(slideModel);

        trackActivity("ClassicTemplate", "Selected", template.id);

        _.defer(() => {
            this.props.closeDialog(true);
        });
    }

    handleCopySlidesFromInspirationGallery = async slide => {
        if (this.isClosing) return;
        this.isClosing = true;

        try {
            await AddSlideController.copySlidesIntoPresentation([slide], this.props.replaceSlide, "gallery");
            this.props.closeDialog(true);
        } catch (err) {
            ShowErrorDialog({
                title: "Unable to copy slide into presentation",
                message: err.message
            });
            this.isClosing = false;
        }
    }

    handleCopySlidesFromPresentation = async slides => {
        if (this.isClosing) return;
        this.isClosing = true;

        try {
            await AddSlideController.copySlidesIntoPresentation(slides, this.props.replaceSlide, "presentation");
            this.props.closeDialog(true);
        } catch (err) {
            ShowErrorDialog({
                title: "Unable to copy slide into presentation",
                message: err.message
            });
            this.isClosing = false;
        }
    }

    handleCopySlideFromLibrary = async teamSlidesDataServiceDataItems => {
        if (this.isClosing) return;
        this.isClosing = true;

        let failedSlideNames = [];
        try {
            await withProgressDialog({
                title: "Adding selected slides to presentation",
                action: async () => {
                    await Promise.all(teamSlidesDataServiceDataItems.map(async teamSlidesDataServiceDataItem => {
                        await AddSlideController.copySlideFromLibraryIntoPresentation(teamSlidesDataServiceDataItem.id, teamSlidesDataServiceDataItem.libraryItemId)
                            .catch(err => {
                                if (err.message.includes("Slide has already been added")) {
                                    failedSlideNames.push(teamSlidesDataServiceDataItem.name);
                                }
                            });
                    }));
                }
            });
            if (failedSlideNames.length > 0) {
                throw new Error(`The following slides have already been added to this presentation: ${failedSlideNames.join(", ")}`);
            }
            this.props.closeDialog(true);
        } catch (err) {
            // if there were any slides added successfully, we should still send them to the editor
            if (failedSlideNames.length < teamSlidesDataServiceDataItems.length) {
                ShowMessageDialog({
                    title: "We've added only the Shared Slides that weren't already in your presentation.",
                    message: <>
                        Shared slides can't be duplicated within the same presentation. The following slides were not added:<br />
                        <ul>
                            {failedSlideNames.map(name => <li key={name}>{name}</li>)}
                        </ul>
                    </>,
                    dialogContentProps: {
                        style: { paddingBottom: 0 }
                    }
                });
                this.props.closeDialog(true);
            } else {
                ShowErrorDialog({
                    title: "Oops! We couldn’t add the following Shared Slides to your presentation.",
                    message: <>
                        These slides have already been added to your presentation. Shared Slides cannot be added more than once.<br />
                        <ul>
                            {failedSlideNames.map(name => <li key={name}>{name}</li>)}
                        </ul>
                    </>
                });
                this.isClosing = false;
            }
        }
    }

    handlePPTImportFinished = () => {
        if (this.isClosing) return;
        this.isClosing = true;
        if (PresentationEditorController.currentSlide) {
            PresentationEditorController.goNextSlide();
        }
        this.props.closeDialog(true);
    }

    handleGeneratedSlide = async (slideModel, prompt, aiRequest) => {
        if (this.isClosing) return;
        this.isClosing = true;

        const slides = await AddSlideController.addSlideFromModel(slideModel);

        const { imageStyle, imageStylePrompt } = PresentationEditorController.presentation.get("metadata") || {};
        trackActivity("Slide", "Generate", null, null, {
            presentation_id: PresentationEditorController.presentation.id,
            slide_id: slides[0].id,
            prompt,
            language: aiRequest.language,
            used_ai_images: imageStyle && imageStyle !== "stock",
            model_name: (!imageStyle || imageStyle === "stock") ? null : AiImageModel.IMAGEN_4_FAST,
            custom_style_type: (!imageStyle || imageStyle === "stock") ? null
                : imageStyle === "custom" ? "prompt" : "preset",
            custom_style_type_prompt: (!imageStyle || imageStyle === "stock") ? null
                : imageStylePrompt,
            context_source: aiRequest.contextUrls?.length ? "webpage" : aiRequest.files?.length ? "file" : undefined,
            use_presentation_as_context: !!aiRequest.presentationAsContext
        });

        _.defer(() => {
            this.props.closeDialog(true);
        });
    }

    renderSelectedPane = () => {
        const { forTeamSlide = false } = this.props;
        const { selectedPaneId } = this.state;

        const workspaceId = AppController.workspaceId;

        trackActivity("AddSlide", "PaneSelected", null, null, { type: selectedPaneId }, { audit: false, skipAmplitude: true });

        switch (selectedPaneId) {
            case "slide-gallery":
                return (
                    <SlideGalleryPane
                        onSelected={this.handleCopySlidesFromInspirationGallery}
                        onAddBlankClassicSlide={this.handleAddBlankClassicSlide}
                        showUserTheme={this.state.showUserTheme}
                        workspaceId={workspaceId}
                        searchType="SlideGalleryPane"
                    />
                );
            case "copy-slide":
                return (
                    <CopySlidePane
                        forTeamSlide={forTeamSlide}
                        onSelected={this.handleCopySlidesFromPresentation}
                        onAddBlankClassicSlide={this.handleAddBlankClassicSlide}
                        showUserTheme={this.state.showUserTheme}
                        workspaceId={workspaceId}
                    />
                );
            case "slide-team-templates":
                return (
                    <SharedSlidesPane
                        isTemplate={true}
                        resourceType={TeamResourceTypes.SLIDE_TEMPLATES}
                        trackActivity="slideTemplateLibrary"
                        workspaceId={workspaceId}
                        searchType="TeamSlideLibraryPane"
                        onSelected={this.handleAddSlideFromTeamSlideTemplate}
                        onToggleUserTheme={this.handleToggleUserTheme}
                        showUserTheme={this.state.showUserTheme}
                        onAddBlankClassicSlide={this.handleAddBlankClassicSlide}
                    />
                );
            case "slide-library":
                return (
                    <SharedSlidesPane
                        isTemplate={false}
                        resourceType={TeamResourceTypes.SHARED_SLIDES}
                        trackActivity="SharedSlideLibrary"
                        workspaceId={workspaceId}
                        searchType="TeamSlideLibraryPane"
                        onSelected={this.handleCopySlideFromLibrary}
                        showUserTheme={this.state.showUserTheme}
                        onAddBlankClassicSlide={this.handleAddBlankClassicSlide}
                    />
                );
            case "ppt-import":
                return (
                    <ImportSlidesFromPPTPane
                        forTeamSlide={forTeamSlide}
                        onImportFinished={this.handlePPTImportFinished}
                    />
                );
            case "ai-generate":
                return (
                    <GenerateSlidePane onGeneratedSlide={this.handleGeneratedSlide} forTeamSlide={forTeamSlide} />
                );
            case "search":
            default:
                return (
                    <SearchSlidesPane
                        forTeamSlide={forTeamSlide}
                        ref={this.searchSlidesPaneRef}
                        workspaceId={workspaceId}
                        onTemplateSelected={this.handleAddSlideFromTemplate}
                        onClassicTemplateSelected={this.handleAddSlideFromClassicTemplate}
                        onGallerySlideSelected={this.handleCopySlidesFromInspirationGallery}
                        onCopySlideSelected={this.handleCopySlidesFromPresentation}
                        onTeamSlideSelected={this.handleCopySlideFromLibrary}
                        onTeamSlideTemplateSelected={this.handleAddSlideFromTeamSlideTemplate}
                        onSelectViewAll={this.handleSelectPane}
                        onAddBlankClassicSlide={this.handleAddBlankClassicSlide}
                        onToggleUserTheme={this.handleToggleUserTheme}
                        // To show always thumbnails with the current theme
                        showUserTheme={true}
                        hideUseCurrentThemeSwitch={this.props.hideUseCurrentThemeSwitch}
                    />
                );
        }
    }

    render() {
        const { closeDialog, hidePPTImport = false, forceShowTeamSlides = false } = this.props;
        const { selectedPaneId } = this.state;

        const workspaceId = AppController.workspaceId;

        const showTeamSlidesPane = (forceShowTeamSlides || (!PresentationEditorController.isSingleSlideEditor));

        const designerBotAvailable = WorkspaceController.actionPermissions[ActionPermissionsObject.GENERATIVE_AI].use;
        const designerBotVisible = designerBotAvailable || WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.GENERATIVE_AI, "use");
        const pptImportHidden = hidePPTImport || !WorkspaceController.actionPermissions[ActionPermissionsObject.IMPORT_PPT].use;
        const canUpgradeToUseTeamSlides = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, "use");

        return (
            <UIContainer isOpen={true} className="add-slide-container" onClose={closeDialog}
                direction="horizontal">
                <UINavigation
                    selected={selectedPaneId}
                    onSelect={id => this.handleSelectPane(id)}
                    onBack={this.handleBackButton}
                >
                    <UINavigationItem
                        key="search"
                        id="search"
                        title="Search For A Slide"
                        icon="search"
                    />
                    {designerBotVisible &&
                        <Divider margin={10} color="#555" />
                    }
                    {designerBotVisible &&
                        <UINavigationItem
                            key="ai"
                            id="ai-generate"
                            title="Generate with AI..."
                            image={<AIIcon />}
                            proBadge={
                                <ProBadge
                                    upgradeType={UpgradePlanDialogType.UPGRADE_PLAN}
                                    show={WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.GENERATIVE_AI, "use")}
                                    analytics={{ cta: "SlideBot", ...ds.selection?.presentation?.getAnalytics() }}
                                    workspaceId={workspaceId}
                                />
                            }
                        />
                    }
                    {
                        WorkspaceController.actionPermissions[ActionPermissionsObject.INSPIRATION_SLIDES].use &&
                        <UINavigationItem
                            key="slide-gallery"
                            id="slide-gallery"
                            title="Get Inspiration"
                            icon="lightbulb"
                        />
                    }
                    {
                        // Adding components in an array in order to avoid wrapping it with an empty fragment or have three conditions
                        showTeamSlidesPane && ([
                            <Divider color="#555" margin={10} key="divider" />,
                            <UINavigationItem
                                key="slide-team-templates"
                                id="slide-team-templates"
                                title="Slide Templates"
                                icon={!canUpgradeToUseTeamSlides && "library_add"}
                                image={canUpgradeToUseTeamSlides && <UpgradeIcon fontSize="medium"/>}
                            />,
                            <UINavigationItem
                                key="slide-library"
                                id="slide-library"
                                title="Shared Slides"
                                icon={!canUpgradeToUseTeamSlides && "people"}
                                image={canUpgradeToUseTeamSlides && <UpgradeIcon fontSize="medium"/>}
                            />
                        ].map(item => item))
                    }
                    {<Divider color="#555" margin={10} />}
                    {
                        <UINavigationItem
                            key="copy-slide"
                            id="copy-slide"
                            title="My Slides"
                            icon="file_copy"
                        />
                    }
                    {!pptImportHidden && <Divider color="#555" margin={10} />}
                    {!pptImportHidden &&
                        <UINavigationItem
                            key="ppt-import"
                            id="ppt-import"
                            title="Import Slides From PPT"
                            icon="upload"
                        />
                    }
                </UINavigation>
                {this.renderSelectedPane()}
            </UIContainer>
        );
    }
}

export default WorkspaceController.withInitializedState(AppController.withState(AddSlideContainer));

